import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import outreachInboxService, {
  InboxSummary,
  LeadAktion,
  OutreachActivity,
  SyncReport,
  ThreadAntwort,
  PostfachNachricht,
} from '../services/outreachInboxService';
import crmService from '../services/crmService';
import taskService from '../services/taskService';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm, StatusTone } from '../components/ui';
import { Icon } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { KpiRow } from '../vertrieb/KpiRow';
import { useUrlParams } from '../vertrieb/useUrlParams';
import { useKampagnenZuordnung, KampagneVerweis } from '../vertrieb/kampagnen';
import { zeit, fehlerText } from '../vertrieb/format';
import { RechteHinweis } from '../vertrieb/RechteHinweis';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';

type Ansicht = 'offen' | 'antworten' | 'alle';

const ANSICHTEN: { id: Ansicht; label: string }[] = [
  { id: 'offen', label: 'Offen' },
  { id: 'antworten', label: 'Alle Antworten' },
  { id: 'alle', label: 'Alles' },
];

/** Deutsche Bezeichnung und Farbe je Lemlist-Typ. */
const TYP_ANZEIGE: Record<string, { label: string; tone: StatusTone }> = {
  emailsReplied: { label: 'E-Mail-Antwort', tone: 'success' },
  linkedinReplied: { label: 'LinkedIn-Antwort', tone: 'success' },
  emailsInterested: { label: 'Interesse', tone: 'success' },
  linkedinInterested: { label: 'Interesse', tone: 'success' },
  meetingBooked: { label: 'Termin gebucht', tone: 'success' },
  emailsNotInterested: { label: 'Kein Interesse', tone: 'neutral' },
  linkedinNotInterested: { label: 'Kein Interesse', tone: 'neutral' },
  manualNotInterested: { label: 'Kein Interesse', tone: 'neutral' },
  emailsUnsubscribed: { label: 'Abgemeldet', tone: 'warning' },
  entityUnsubscribed: { label: 'Abgemeldet', tone: 'warning' },
  emailsBounced: { label: 'Unzustellbar', tone: 'danger' },
  outOfOffice: { label: 'Abwesenheit', tone: 'info' },
};

const typBadge = (typ: string) => TYP_ANZEIGE[typ] || { label: typ, tone: 'neutral' as StatusTone };

const AKTION_LABEL: Record<LeadAktion, string> = {
  interested: 'Als interessiert markiert.',
  'not-interested': 'Als „kein Interesse“ markiert.',
  pause: 'Kampagne für diesen Kontakt pausiert.',
  resume: 'Kampagne für diesen Kontakt fortgesetzt.',
};

const kanalLabel = (k?: string) => (k === 'linkedin' ? 'LinkedIn' : k === 'email' ? 'E-Mail' : 'Sonstige');

/**
 * Antworten und Reaktionen aus den Kampagnen. Ansicht und geoeffneter Eintrag
 * stehen in der URL (?ansicht=, ?eintrag=), damit Akte und Aufgaben direkt
 * auf eine Antwort verlinken koennen.
 *
 * Welle 2: Abgleich, Erledigt-Haken, Vorgang und Aufgabe sind
 * Schreibaufrufe (vertrieb.bearbeiten); Lesende sehen Liste, Verlauf und
 * Postfach-Volltext. Antworten und Lead-Zustand gehen ueber Lemlist nach
 * aussen und verlangen versand.ausfuehren (Kapitel 4.2: Mitarbeit und
 * Marketing versenden nicht).
 */
/** Lead in Leads & Outreach: per ID, sonst ueber die Adresse gesucht. */
const leadPfad = (e: { leadId?: string | null; leadEmail?: string | null }) => (e.leadId
  ? `/lead-sync?lead=${encodeURIComponent(e.leadId)}`
  : e.leadEmail ? `/lead-sync?suche=${encodeURIComponent(e.leadEmail)}` : null);

const OutreachInboxPage: React.FC = () => {
  const { push } = useToast();
  const { prompt } = useConfirm();
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const darfVersenden = darf('versand.ausfuehren');
  const { get, set } = useUrlParams();
  const zuordnung = useKampagnenZuordnung();
  const ansicht = (['offen', 'antworten', 'alle'].includes(get('ansicht')) ? get('ansicht') : 'offen') as Ansicht;
  const eintragParam = get('eintrag') || null;

  const [eintraege, setEintraege] = useState<OutreachActivity[]>([]);
  const [uebersicht, setUebersicht] = useState<InboxSummary | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [gleichtAb, setGleichtAb] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const [ausgewaehlt, setAusgewaehlt] = useState<OutreachActivity | null>(null);
  const [verlauf, setVerlauf] = useState<ThreadAntwort | null>(null);
  const [antwort, setAntwort] = useState('');
  const [sendet, setSendet] = useState(false);
  const [neuerVorgang, setNeuerVorgang] = useState<{ id: string; title: string } | null>(null);

  // Volltext aus dem echten Postfach -- je Eintrag einzeln, auf Klick.
  const [postfachFuer, setPostfachFuer] = useState<string | null>(null);
  const [postfachTexte, setPostfachTexte] = useState<PostfachNachricht[]>([]);
  const [postfachLaedt, setPostfachLaedt] = useState(false);
  const [postfachFehler, setPostfachFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const filter = ansicht === 'offen'
        ? { handled: false, nurAntworten: true, limit: 200 }
        : ansicht === 'antworten'
          ? { nurAntworten: true, limit: 200 }
          : { limit: 300 };
      const [liste, stand] = await Promise.all([
        outreachInboxService.getActivities(filter),
        outreachInboxService.getSummary(),
      ]);
      setEintraege(liste ?? []);
      setUebersicht(stand ?? null);
    } catch (e) {
      setFehler(fehlerText(e, 'Laden fehlgeschlagen'));
    } finally {
      setLaedt(false);
    }
  }, [ansicht]);

  useEffect(() => { laden(); }, [laden]);

  // Deep-Link ?eintrag=<id>: Verlauf laden, auch wenn der Eintrag nicht in
  // der aktuellen Ansicht liegt.
  useEffect(() => {
    if (!eintragParam) {
      setAusgewaehlt(null);
      setVerlauf(null);
      return;
    }
    let aktiv = true;
    setVerlauf(null);
    setAntwort('');
    setNeuerVorgang(null);
    const ausListe = eintraege.find((e) => e.id === eintragParam);
    if (ausListe) setAusgewaehlt(ausListe);
    Promise.resolve()
      .then(() => outreachInboxService.getThread(eintragParam))
      .then((t) => {
        if (!aktiv || !t) return;
        setVerlauf(t);
        if (t.eintrag) setAusgewaehlt(t.eintrag);
      })
      .catch((e) => { if (aktiv) setFehler(fehlerText(e, 'Verlauf konnte nicht geladen werden')); });
    return () => { aktiv = false; };
    // eintraege absichtlich nicht als Abhaengigkeit: nur bei URL-Wechsel laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eintragParam]);

  const postfachLesen = async (id: string) => {
    setPostfachFuer(id);
    setPostfachTexte([]);
    setPostfachFehler(null);
    setPostfachLaedt(true);
    try {
      const d = await outreachInboxService.postfach(id);
      setPostfachTexte(d?.nachrichten || []);
    } catch (e) {
      setPostfachFehler(fehlerText(e, 'Postfach nicht lesbar'));
    } finally {
      setPostfachLaedt(false);
    }
  };

  const abgleichen = async () => {
    setGleichtAb(true);
    setFehler(null);
    try {
      const bericht: SyncReport = await outreachInboxService.sync();
      push({
        message: `${bericht.kampagnen} Kampagnen geprüft, ${bericht.relevant} Reaktionen gefunden, `
          + `${bericht.neu} neu, ${bericht.aktualisiert} aufgefrischt`
          + (bericht.gesperrt ? `, ${bericht.gesperrt} Adressen gesperrt` : '')
          + (bericht.fehler.length ? ` – ${bericht.fehler.length} Fehler` : ''),
        severity: bericht.fehler.length ? 'warning' : 'success',
        duration: 8000,
      });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Abgleich fehlgeschlagen'));
    } finally {
      setGleichtAb(false);
    }
  };

  const oeffnen = (eintrag: OutreachActivity) => set('eintrag', eintrag.id);
  const schliessen = () => set('eintrag', null, { replace: true });

  const umschalten = async (eintrag: OutreachActivity) => {
    try {
      const neu = await outreachInboxService.setHandled(eintrag.id, !eintrag.handled);
      setEintraege((alt) => (ansicht === 'offen' && neu.handled
        ? alt.filter((x) => x.id !== neu.id)
        : alt.map((x) => (x.id === neu.id ? neu : x))));
      setUebersicht(await outreachInboxService.getSummary());
      if (ausgewaehlt?.id === neu.id) setAusgewaehlt(neu);
    } catch (e) {
      setFehler(fehlerText(e, 'Änderung fehlgeschlagen'));
    }
  };

  const senden = async () => {
    if (!ausgewaehlt || !antwort.trim()) return;
    setSendet(true);
    setFehler(null);
    try {
      await outreachInboxService.reply(ausgewaehlt.id, antwort.trim());
      push({ message: 'Antwort gesendet.', severity: 'success' });
      setAntwort('');
      await laden();
      setVerlauf(await outreachInboxService.getThread(ausgewaehlt.id));
    } catch (e) {
      setFehler(fehlerText(e, 'Senden fehlgeschlagen'));
    } finally {
      setSendet(false);
    }
  };

  const zustand = async (aktion: LeadAktion) => {
    if (!ausgewaehlt) return;
    try {
      await outreachInboxService.setLeadState(ausgewaehlt.id, aktion);
      push({ message: AKTION_LABEL[aktion], severity: 'success' });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Zustand konnte nicht gesetzt werden'));
    }
  };

  const vorgangAnlegen = async () => {
    if (!ausgewaehlt) return;
    try {
      const ergebnis = await crmService.dealFromActivity(ausgewaehlt.id);
      setNeuerVorgang({ id: ergebnis.vorgang.id, title: ergebnis.vorgang.title });
      push({
        message: ergebnis.neu
          ? `Vorgang „${ergebnis.vorgang.title}“ in der Pipeline angelegt.`
          : `Es gibt bereits einen offenen Vorgang: „${ergebnis.vorgang.title}“.`,
        severity: ergebnis.neu ? 'success' : 'info',
      });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Vorgang konnte nicht angelegt werden'));
    }
  };

  const aufgabeAnlegen = async () => {
    if (!ausgewaehlt) return;
    const titel = await prompt({
      title: 'Aufgabe zu dieser Antwort',
      label: 'Was ist zu tun?',
      defaultValue: `Antwort an ${ausgewaehlt.leadName || ausgewaehlt.leadEmail || 'Kontakt'}`,
      required: true,
      confirmLabel: 'Anlegen',
    });
    if (!titel) return;
    try {
      await taskService.create({
        title: titel.trim(),
        activityId: ausgewaehlt.id,
        leadId: ausgewaehlt.leadId || undefined,
        companyId: ausgewaehlt.companyId || undefined,
        dueAt: new Date().toISOString(),
      });
      push({ message: 'Aufgabe für heute angelegt.', severity: 'success' });
    } catch (e) {
      setFehler(fehlerText(e, 'Aufgabe konnte nicht angelegt werden'));
    }
  };

  const firmenLink = (e: OutreachActivity) => {
    if (e.companyId) return <Link to={`/companies/${e.companyId}`}>{e.companyName || 'Firmenakte'}</Link>;
    if (e.companyName) return <Link to={`/companies?suche=${encodeURIComponent(e.companyName)}`} title="Firma suchen">{e.companyName}</Link>;
    return null;
  };

  return (
    <div className="page-container-wide">
      <SalesProcessBar stepId="antworten" />
      <PageHeader
        title="Antworten & Reaktionen"
        subtitle="Was aus den laufenden Kampagnen zurückkommt – lesen, beantworten, in die Pipeline übernehmen. Der Bearbeitungsstand wird hier geführt; Lemlist kennt ihn nicht."
        actions={darfBearbeiten ? (
          <button type="button" className="btn btn-primary" onClick={abgleichen} disabled={gleichtAb}>
            <Icon name="refresh" size={14} /> {gleichtAb ? 'Gleicht ab…' : 'Mit Lemlist abgleichen'}
          </button>
        ) : undefined}
      />
      <RechteHinweis was="Abgleichen und Antworten bearbeiten" />

      {uebersicht && (
        <KpiRow
          aria-label="Antworten-Kennzahlen"
          items={[
            { label: 'offene Antworten', value: uebersicht.offeneAntworten, tone: uebersicht.offeneAntworten ? 'warn' : 'neutral', to: '/outreach-inbox?ansicht=offen' },
            { label: 'Antworten gesamt', value: uebersicht.alleAntworten, to: '/outreach-inbox?ansicht=antworten' },
            { label: 'kein Interesse', value: uebersicht.nichtInteressiert },
            { label: 'unzustellbar', value: uebersicht.unzustellbar, tone: uebersicht.unzustellbar ? 'warn' : 'neutral' },
          ]}
        />
      )}

      {fehler && <div className="alert alert-error">{fehler}</div>}

      <div className="tabs" role="tablist" aria-label="Ansicht">
        {ANSICHTEN.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={ansicht === a.id}
            className={`tab ${ansicht === a.id ? 'tab-active' : ''}`}
            onClick={() => set('ansicht', a.id === 'offen' ? null : a.id, { replace: true })}
          >
            {a.label}
          </button>
        ))}
      </div>

      {laedt ? (
        <LoadingState rows={4} label="Antworten werden geladen" />
      ) : eintraege.length === 0 && !ausgewaehlt ? (
        <EmptyState
          icon="inbox"
          title={ansicht === 'offen' ? 'Keine offenen Antworten' : 'Nichts vorhanden'}
          description={!darfBearbeiten
            ? 'Neue Reaktionen holt der stündliche Abgleich.'
            : ansicht === 'offen'
              ? 'Alles bearbeitet. Neue Reaktionen holt „Mit Lemlist abgleichen“ – oder der stündliche Lauf.'
              : 'Wenn die Kampagnen laufen, hole die Reaktionen mit „Mit Lemlist abgleichen“.'}
          action={darfBearbeiten ? { label: 'Mit Lemlist abgleichen', onClick: abgleichen } : undefined}
          secondaryAction={ansicht === 'offen' ? { label: 'Alle Antworten', onClick: () => set('ansicht', 'antworten', { replace: true }) } : undefined}
        />
      ) : (
        <div className="inbox-layout">
          <div className="inbox-list" role="list" aria-label="Reaktionen">
            {eintraege.map((e) => {
              const badge = typBadge(e.type);
              return (
                <div
                  key={e.id}
                  role="listitem"
                  className={`inbox-item ${e.handled ? 'inbox-item-handled' : ''} ${ausgewaehlt?.id === e.id ? 'inbox-item-aktiv' : ''}`}
                  onClick={() => oeffnen(e)}
                >
                  <div className="inbox-item-head">
                    <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>
                    <span className="inbox-kanal">{kanalLabel(e.kanal)}</span>
                    <span className="inbox-datum">{zeit(e.occurredAt)}</span>
                    {darfBearbeiten ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={(ev) => { ev.stopPropagation(); umschalten(e); }}
                      >
                        {e.handled ? 'Wieder öffnen' : 'Erledigt'}
                      </button>
                    ) : e.handled && <StatusBadge tone="neutral">erledigt</StatusBadge>}
                  </div>
                  <div className="inbox-item-lead" onClick={(ev) => ev.stopPropagation()}>
                    {leadPfad(e)
                      ? <Link to={leadPfad(e) as string} title="Lead öffnen"><strong>{e.leadName || e.leadEmail}</strong></Link>
                      : <strong>{e.leadName || e.leadEmail || 'Unbekannt'}</strong>}
                    {(e.companyId || e.companyName) && <> · {firmenLink(e)}</>}
                  </div>
                  {(e.campaignId || e.campaignName) && (
                    <div className="inbox-item-kampagne" onClick={(ev) => ev.stopPropagation()}>
                      <KampagneVerweis lemlistId={e.campaignId} name={e.campaignName} zuordnung={zuordnung} />
                    </div>
                  )}
                  {e.text && <blockquote className="inbox-item-text">{e.text}</blockquote>}
                  {/* Lemlist liefert bei E-Mail-Antworten oft nur Betreff und
                      erste Zeile -- der Volltext liegt im echten Postfach. */}
                  {!e.text && e.kanal === 'email' && e.leadEmail && (
                    <div className="inbox-postfach">
                      {postfachFuer === e.id && postfachLaedt && <span>Postfach wird gelesen…</span>}
                      {postfachFuer === e.id && postfachFehler && <span className="postfach-fehler">{postfachFehler}</span>}
                      {postfachFuer === e.id && !postfachLaedt && !postfachFehler && (
                        postfachTexte.length
                          ? postfachTexte.map((n) => (
                            <blockquote key={n.uid} className="inbox-item-text">
                              {n.subject && <strong>{n.subject}</strong>}
                              {n.text || '(kein Text)'}
                            </blockquote>
                          ))
                          : <span>Nichts zu dieser Adresse im Postfach gefunden.</span>
                      )}
                      {postfachFuer !== e.id && (
                        <button type="button" className="btn btn-sm btn-ghost" onClick={(ev) => { ev.stopPropagation(); postfachLesen(e.id); }}>
                          Volltext aus dem Postfach
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {ausgewaehlt && (
            <div className="inbox-detail" aria-label="Details zur Antwort">
              <div className="inbox-detail-kopf">
                <div>
                  {leadPfad(ausgewaehlt)
                    ? <Link to={leadPfad(ausgewaehlt) as string} title="Lead öffnen"><strong>{ausgewaehlt.leadName || ausgewaehlt.leadEmail}</strong></Link>
                    : <strong>{ausgewaehlt.leadName || ausgewaehlt.leadEmail}</strong>}
                  {(ausgewaehlt.companyId || ausgewaehlt.companyName) && <span> · {firmenLink(ausgewaehlt)}</span>}
                  <div className="inbox-detail-meta">
                    {ausgewaehlt.leadEmail}
                  </div>
                  {(ausgewaehlt.campaignId || ausgewaehlt.campaignName) && (
                    <KampagneVerweis lemlistId={ausgewaehlt.campaignId} name={ausgewaehlt.campaignName} zuordnung={zuordnung} leads className="mt-1" />
                  )}
                </div>
                <button type="button" className="btn btn-sm btn-ghost" onClick={schliessen} aria-label="Details schließen">
                  <Icon name="close" size={16} />
                </button>
              </div>

              {darfBearbeiten && (
                <div className="inbox-aktionen">
                  <button type="button" className="btn btn-sm btn-primary" onClick={vorgangAnlegen}>
                    <Icon name="kanban" size={14} /> In die Pipeline
                  </button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={aufgabeAnlegen}>
                    <Icon name="check" size={14} /> Aufgabe
                  </button>
                  {/* Lead-Zustand setzt die Lemlist-Sequenz fort oder haelt sie an (versand.ausfuehren). */}
                  {darfVersenden && (
                    <>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => zustand('interested')}>Interessiert</button>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => zustand('not-interested')}>Kein Interesse</button>
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => zustand('pause')}>Pausieren</button>
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => zustand('resume')}>Fortsetzen</button>
                    </>
                  )}
                </div>
              )}
              {darfBearbeiten && (
                <RechteHinweis recht="versand.ausfuehren" was="Antworten senden und Lead-Status in Lemlist setzen" />
              )}

              {neuerVorgang && (
                <div className="flow-banner flow-banner--done" role="status">
                  <Icon name="check" size={16} />
                  <span>Vorgang „{neuerVorgang.title}“ steht in der Pipeline.</span>
                  <Link to={`/pipeline?deal=${neuerVorgang.id}`} className="btn btn-primary btn-sm">
                    Vorgang öffnen <Icon name="arrow-right" size={14} />
                  </Link>
                </div>
              )}

              <h3>Verlauf</h3>
              {!verlauf && <LoadingState compact rows={2} label="Verlauf wird geladen" />}
              {verlauf?.hinweis && <p className="inbox-item-hinweis">{verlauf.hinweis}</p>}
              {verlauf && verlauf.nachrichten.length === 0 && !verlauf.hinweis && (
                <p className="inbox-item-hinweis">Kein Verlauf vorhanden.</p>
              )}
              <div className="thread">
                {(verlauf?.nachrichten || []).map((n, i) => (
                  <div key={n._id || i} className={`thread-nachricht ${n.direction === 'out' ? 'thread-aus' : 'thread-ein'}`}>
                    <div className="thread-kopf">
                      <span>{n.from || (n.direction === 'out' ? 'Wir' : 'Gegenüber')}</span>
                      <span>{zeit(n.sentAt || n.createdAt, '')}</span>
                    </div>
                    {n.subject && <div className="thread-betreff">{n.subject}</div>}
                    <div className="thread-text">{n.text || n.body}</div>
                  </div>
                ))}
              </div>

              {darfVersenden && (
                <>
                  <h3>Antworten</h3>
                  <p className="inbox-item-hinweis">
                    {ausgewaehlt.kanal === 'linkedin'
                      ? 'Wird als LinkedIn-Nachricht gesendet.'
                      : 'Wird im bestehenden E-Mail-Verlauf gesendet, Betreff bleibt erhalten.'}
                  </p>
                  <textarea
                    className="form-textarea"
                    rows={6}
                    value={antwort}
                    onChange={(e) => setAntwort(e.target.value)}
                    placeholder="Antwort schreiben…"
                    aria-label="Antworttext"
                  />
                  <button type="button" className="btn btn-primary" onClick={senden} disabled={sendet || !antwort.trim()}>
                    <Icon name="send" size={14} /> {sendet ? 'Sendet…' : 'Antwort senden'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OutreachInboxPage;
